import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * A custom route component to protect routes that require authentication.
 * If the user is not authenticated, they are redirected to the login page.
 * * @param {object} props
 * @param {React.ReactNode} props.element - The component to render if the user is authenticated.
 */
const ProtectedRoute = ({ element }) => {
  const navigate = useNavigate();
  // Placeholder for user authentication state.
  // In a real application, you would check for a user context, a token in localStorage, or an API call.
  // For this example, we assume the user is authenticated.
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    // Simulate an async authentication check.
    const checkAuth = async () => {
      try {
        // Replace this with your actual authentication logic, e.g., checking for a JWT.
        const token = localStorage.getItem("xenios_cyberguard_token");
        if (token) {
          // If a token exists, assume authenticated for now.
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Authentication check failed:", error);
        setIsAuthenticated(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    // If auth check is complete and not authenticated, redirect to the login page.
    if (!isCheckingAuth && !isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isCheckingAuth, isAuthenticated, navigate]);

  // Render a loading state while checking authentication.
  if (isCheckingAuth) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  // If authenticated, render the requested component.
  return isAuthenticated ? element : null;
};

export default ProtectedRoute;
